import React, { useEffect, useMemo, useState } from "react";
import { createRoot } from "react-dom/client";
import { createClient } from "@supabase/supabase-js";
import jsPDF from "jspdf";
import { PDFDocument } from "pdf-lib";
import "./styles.css";

const APP_VERSION = "Vorkasse-Endabrechnung v2 - 04.08.2026";

const supabaseUrl = window.CASA_TUA_SUPABASE_URL || window.SUPABASE_URL || window.REACT_APP_SUPABASE_URL;
const supabaseKey = window.CASA_TUA_SUPABASE_ANON_KEY || window.SUPABASE_ANON_KEY || window.REACT_APP_SUPABASE_ANON_KEY;

const TABLES = {
  work_logs: "Stunden / Tagebuch",
  costs: "Baukosten / Rechnungen",
  orders: "Bestellungen"
};

const BILLING_TYPES = [
  { value: "vorkasse", label: "Vorkasse" },
  { value: "offerte", label: "Offerte" },
  { value: "rechnung", label: "Rechnung" },
  { value: "honorarrechnung", label: "Honorarrechnung" }
];

const STATUS_OPTIONS = ["offen", "eingereicht", "geprüft", "abgerechnet", "abgelehnt"];
const PAYMENT_OPTIONS = ["offen", "vorkasse", "bezahlt", "storniert"];

function fmt(n) {
  return "CHF " + Number(n || 0).toLocaleString("de-CH", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
function safe(v) { return v === null || v === undefined ? "" : String(v); }
function today() { return new Date().toISOString().slice(0, 10); }
function monthStart() { const d=new Date(); return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0,10); }
function monthEnd() { const d=new Date(); return new Date(d.getFullYear(), d.getMonth()+1, 0).toISOString().slice(0,10); }

const emptyForms = {
  work_logs: { work_date: today(), person_name: "", company_name: "CDMC Gastro AG", area: "", activity: "", hours: "", hourly_rate: "50", status: "offen", billing_type: "honorarrechnung", notes: "" },
  costs: { cost_date: today(), supplier: "", person_name: "", company_name: "", category: "", description: "", invoice_no: "", net_amount: "", vat_amount: "", payment_status: "offen", status: "offen", billing_type: "rechnung" },
  orders: { order_date: today(), supplier: "", person_name: "", company_name: "", category: "", description: "", ordered_by: "", expected_delivery: "", delivered_date: "", amount: "", order_status: "bestellt", status: "offen", payment_status: "offen", billing_type: "offerte" }
};

function createSupabaseClient() {
  if (!supabaseUrl || !supabaseKey) return null;
  return createClient(supabaseUrl, supabaseKey);
}

const supabase = createSupabaseClient();

function App() {
  const [session, setSession] = useState(null);
  const [profile, setProfile] = useState(null);
  const [view, setView] = useState("work_logs");
  const [rows, setRows] = useState({ work_logs: [], costs: [], orders: [] });
  const [form, setForm] = useState(emptyForms.work_logs);
  const [editId, setEditId] = useState(null);
  const [file, setFile] = useState(null);
  const [msg, setMsg] = useState("");
  const [loading, setLoading] = useState(false);
  const [periodStart, setPeriodStart] = useState(monthStart());
  const [periodEnd, setPeriodEnd] = useState(monthEnd());

  useEffect(() => {
    if (!supabase) return;
    supabase.auth.getSession().then(({ data }) => setSession(data.session));
    const { data: listener } = supabase.auth.onAuthStateChange((_event, sess) => setSession(sess));
    return () => listener.subscription.unsubscribe();
  }, []);

  useEffect(() => {
    if (!session) { setProfile(null); return; }
    loadProfile();
    loadAll();
  }, [session]);

  useEffect(() => {
    setForm(emptyForms[view]);
    setEditId(null);
    setFile(null);
  }, [view]);

  async function loadProfile() {
    const { data, error } = await supabase.from("profiles").select("*").eq("id", session.user.id).maybeSingle();
    if (!error) setProfile(data);
  }

  async function loadAll() {
    setLoading(true);
    const [w, c, o] = await Promise.all([
      supabase.from("work_logs").select("*").order("work_date", { ascending: false }),
      supabase.from("costs").select("*").order("cost_date", { ascending: false }),
      supabase.from("orders").select("*").order("order_date", { ascending: false })
    ]);
    setRows({
      work_logs: w.data || [],
      costs: c.data || [],
      orders: o.data || []
    });
    setLoading(false);
  }

  function showError(error) {
    console.error(error);
    setMsg(error?.message || String(error));
  }

  async function uploadFile(table, idHint) {
    if (!file) return {};
    const ext = file.name.split(".").pop() || "pdf";
    const path = `${table}/${session.user.id}/${Date.now()}_${idHint || "beleg"}.${ext}`;
    const { error } = await supabase.storage.from("documents").upload(path, file, { upsert: true });
    if (error) throw error;
    return { receipt_path: path, receipt_url: path };
  }

  function cleanPayload(table, values) {
    const p = { ...values };
    Object.keys(p).forEach(k => { if (p[k] === "") p[k] = null; });
    if (table === "work_logs") {
      p.hours = Number(p.hours || 0);
      p.hourly_rate = Number(p.hourly_rate || 0);
      p.person_name = p.person_name || profile?.full_name || session.user.email;
      p.company_name = p.company_name || "CDMC Gastro AG";
    }
    if (table === "costs") {
      p.net_amount = Number(p.net_amount || 0);
      p.vat_amount = Number(p.vat_amount || 0);
      p.supplier = p.supplier || p.company_name || "Lieferant";
      p.payment_status = p.payment_status || "offen";
    }
    if (table === "orders") {
      p.amount = Number(p.amount || 0);
      p.supplier = p.supplier || p.company_name || "Lieferant";
      p.payment_status = p.payment_status || "offen";
    }
    p.billing_type = p.billing_type || (table === "work_logs" ? "honorarrechnung" : "rechnung");
    p.created_by = session.user.id;
    return p;
  }

  async function saveEntry(e) {
    e.preventDefault();
    try {
      setLoading(true);
      setMsg("");
      let payload = cleanPayload(view, form);
      const upload = await uploadFile(view, editId || "neu");
      payload = { ...payload, ...upload };
      let res;
      if (editId) res = await supabase.from(view).update(payload).eq("id", editId).select().single();
      else res = await supabase.from(view).insert(payload).select().single();
      if (res.error) throw res.error;
      setForm(emptyForms[view]);
      setEditId(null);
      setFile(null);
      const input = document.getElementById("receipt_file");
      if (input) input.value = "";
      await loadAll();
      setMsg(editId ? "Eintrag aktualisiert." : "Eintrag gespeichert.");
    } catch (err) { showError(err); }
    finally { setLoading(false); }
  }

  function editRow(table, r) {
    setView(table);
    const next = {};
    Object.keys(emptyForms[table]).forEach(k => next[k] = r[k] ?? emptyForms[table][k] ?? "");
    if (table === "work_logs") {
      next.work_date = r.work_date || today();
      next.hours = r.hours ?? "";
      next.hourly_rate = r.hourly_rate ?? "50";
    }
    if (table === "costs") {
      next.cost_date = r.cost_date || today();
      next.net_amount = r.net_amount ?? "";
      next.vat_amount = r.vat_amount ?? "";
    }
    if (table === "orders") {
      next.order_date = r.order_date || today();
      next.amount = r.amount ?? "";
    }
    setForm(next);
    setEditId(r.id);
    setFile(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  async function deleteRow(table, id) {
    if (!window.confirm("Eintrag wirklich löschen?")) return;
    const { error } = await supabase.from(table).delete().eq("id", id);
    if (error) showError(error); else loadAll();
  }

  async function signedUrl(path) {
    if (!path) return null;
    const { data, error } = await supabase.storage.from("documents").createSignedUrl(path, 60 * 10);
    if (error) return null;
    return data.signedUrl;
  }

  async function openReceipt(path) {
    const url = await signedUrl(path);
    if (!url) return setMsg("Beleg konnte nicht geöffnet werden.");
    window.open(url, "_blank");
  }

  function filtered(table) {
    const dateKey = table === "work_logs" ? "work_date" : table === "costs" ? "cost_date" : "order_date";
    return (rows[table] || []).filter(r => (!periodStart || r[dateKey] >= periodStart) && (!periodEnd || r[dateKey] <= periodEnd));
  }

  function amountFor(table, r) {
    if (table === "work_logs") return Number(r.total_amount ?? (Number(r.hours || 0) * Number(r.hourly_rate || 0)));
    if (table === "costs") return Number(r.gross_amount ?? (Number(r.net_amount || 0) + Number(r.vat_amount || 0)));
    return Number(r.amount || 0);
  }

  function normalizeRows() {
    const out = [];
    for (const table of Object.keys(TABLES)) {
      for (const r of filtered(table)) {
        out.push({
          table,
          type: r.billing_type || (table === "work_logs" ? "honorarrechnung" : "rechnung"),
          date: r.work_date || r.cost_date || r.order_date,
          title: r.person_name || r.supplier || r.company_name || TABLES[table],
          category: table === "work_logs" ? (r.area || "") : (r.category || ""),
          description: table === "work_logs" ? (r.activity || "") : (r.description || ""),
          amount: amountFor(table, r),
          status: r.status || "",
          payment_status: r.payment_status || "",
          receipt_path: r.receipt_path,
          raw: r
        });
      }
    }
    return out;
  }

  async function generateFinalPdf() {
    try {
      setLoading(true);
      setMsg("PDF wird erstellt...");
      const items = normalizeRows();
      const doc = new jsPDF({ unit: "pt", format: "a4" });
      const pageW = doc.internal.pageSize.getWidth();
      let y = 48;

      const addText = (text, x, yy, size=10, style="normal") => {
        doc.setFont("helvetica", style);
        doc.setFontSize(size);
        doc.text(safe(text), x, yy);
      };
      const line = () => { doc.setDrawColor(210); doc.line(40, y, pageW-40, y); y += 14; };
      const ensure = (h=40) => { if (y + h > 790) { doc.addPage(); y = 48; } };

      addText("Casa Tua – Endabrechnung", 40, y, 18, "bold"); y += 24;
      addText(`Zeitraum: ${periodStart} bis ${periodEnd}`, 40, y, 10); y += 20;
      addText("Aufteilung nach Vorkasse, Offerte, Rechnung und Honorarrechnung", 40, y, 10); y += 22;
      line();

      let grand = 0;
      for (const bt of BILLING_TYPES) {
        const part = items.filter(i => i.type === bt.value);
        const total = part.reduce((s,i) => s + i.amount, 0);
        grand += total;
        ensure(60);
        addText(bt.label, 40, y, 14, "bold");
        addText(fmt(total), pageW-160, y, 12, "bold");
        y += 20;

        if (part.length === 0) { addText("Keine Positionen", 55, y); y += 18; continue; }

        for (const i of part) {
          ensure(55);
          addText(`${i.date || ""} · ${TABLES[i.table]} · ${i.title}`, 55, y, 9, "bold"); y += 12;
          addText(`Kategorie: ${i.category || "-"} | Status: ${i.status || "-"}${i.payment_status ? " | Zahlung: "+i.payment_status : ""}`, 55, y, 8); y += 11;
          const split = doc.splitTextToSize(`Beschreibung: ${i.description || "-"}`, pageW - 180);
          doc.setFontSize(8); doc.text(split, 55, y);
          addText(fmt(i.amount), pageW-140, y, 9, "bold");
          y += Math.max(14, split.length * 10) + 5;
        }
        y += 8;
      }

      line();
      addText("Gesamttotal inkl. CDMC-Gastro-Stunden", 40, y, 13, "bold");
      addText(fmt(grand), pageW-160, y, 13, "bold");

      const cdmcHours = items.filter(i => i.table === "work_logs");
      doc.addPage(); y = 48;
      addText("Anhang: Stundenübersicht CDMC Gastro", 40, y, 16, "bold"); y += 24;
      const hTotal = cdmcHours.reduce((s,i) => s + i.amount, 0);
      const hourSum = cdmcHours.reduce((s,i) => s + Number(i.raw.hours || 0), 0);
      addText(`Zeitraum: ${periodStart} bis ${periodEnd}`, 40, y); y += 16;
      addText(`Total Stunden: ${hourSum.toLocaleString("de-CH")} | Total Betrag: ${fmt(hTotal)}`, 40, y, 11, "bold"); y += 22;
      line();
      if (cdmcHours.length === 0) { addText("Keine Stundeneinträge im Zeitraum.", 40, y); y += 20; }
      for (const i of cdmcHours) {
        ensure(45);
        addText(`${i.date} · ${i.raw.person_name || "CDMC Gastro"} · ${i.category || "-"}`, 40, y, 9, "bold"); y += 12;
        addText(`${i.raw.hours || 0} h à ${fmt(i.raw.hourly_rate || 0)} = ${fmt(i.amount)}`, 40, y, 8); y += 11;
        const split = doc.splitTextToSize(i.description || "-", pageW - 80);
        doc.setFontSize(8); doc.text(split, 40, y);
        y += Math.max(14, split.length * 10) + 5;
      }

      const baseArrayBuffer = doc.output("arraybuffer");
      const merged = await PDFDocument.load(baseArrayBuffer);
      let appendixCount = 0;

      for (const i of items.filter(x => x.receipt_path)) {
        const url = await signedUrl(i.receipt_path);
        if (!url) continue;
        try {
          const resp = await fetch(url);
          const bytes = await resp.arrayBuffer();
          const name = i.receipt_path.toLowerCase();
          if (name.endsWith(".pdf")) {
            const other = await PDFDocument.load(bytes);
            const pages = await merged.copyPages(other, other.getPageIndices());
            pages.forEach(p => merged.addPage(p));
            appendixCount += pages.length;
          } else if (name.match(/\.(png|jpg|jpeg)$/)) {
            const img = name.endsWith(".png") ? await merged.embedPng(bytes) : await merged.embedJpg(bytes);
            const page = merged.addPage();
            const { width, height } = page.getSize();
            const scale = Math.min((width-80)/img.width, (height-80)/img.height);
            page.drawImage(img, { x: 40, y: height-40-img.height*scale, width: img.width*scale, height: img.height*scale });
            appendixCount += 1;
          }
        } catch (err) {
          console.error("Beleg konnte nicht angehängt werden", i, err);
        }
      }

      const finalBytes = await merged.save();
      const blob = new Blob([finalBytes], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Casa_Tua_Endabrechnung_${periodStart}_${periodEnd}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      setMsg(`Endabrechnung erstellt. Angefügte Belegseiten: ${appendixCount}.`);
    } catch (err) { showError(err); }
    finally { setLoading(false); }
  }

  if (!supabase) return <div className="page"><h1>Casa Tua Bautagebuch</h1><div className="error">Supabase-Verbindung fehlt. Bitte public/config.js prüfen.</div></div>;
  if (!session) return <Auth />;

  return (
    <div className="page">
      <header className="top">
        <div>
          <h1>Casa Tua Bautagebuch</h1>
          <p className="version">{APP_VERSION}</p>
          <p>{profile?.full_name || session.user.email} · {profile?.role || "Profil lädt..."}</p>
        </div>
        <button onClick={() => supabase.auth.signOut()}>Logout</button>
      </header>

      {profile && !profile.is_active && profile.role !== "superadmin" ? (
        <div className="card warning">Dein Zugang ist registriert, aber noch nicht freigegeben.</div>
      ) : null}

      <div className="tabs">
        <button className={view==="work_logs"?"active":""} onClick={()=>setView("work_logs")}>Stunden / Tagebuch</button>
        <button className={view==="costs"?"active":""} onClick={()=>setView("costs")}>Baukosten / Rechnungen</button>
        <button className={view==="orders"?"active":""} onClick={()=>setView("orders")}>Bestellungen</button>
        <button className={view==="billing"?"active":""} onClick={()=>setView("billing")}>Endabrechnung</button>
        {profile?.role === "superadmin" || profile?.role === "admin" ? <button className={view==="users"?"active":""} onClick={()=>setView("users")}>Zugänge</button> : null}
      </div>

      {msg && <div className="msg">{msg}</div>}
      {loading && <div className="msg">Lädt...</div>}

      {view !== "billing" && view !== "users" ? (
        <>
          <EntryForm table={view} form={form} setForm={setForm} file={file} setFile={setFile} saveEntry={saveEntry} editId={editId} cancelEdit={()=>{setEditId(null); setForm(emptyForms[view]); setFile(null);}} />
          <EntriesTable table={view} rows={rows[view]} editRow={editRow} deleteRow={deleteRow} openReceipt={openReceipt} />
        </>
      ) : null}

      {view === "billing" ? (
        <div className="card">
          <h2>Endabrechnung</h2>
          <div className="grid">
            <label>Von<input type="date" value={periodStart} onChange={e=>setPeriodStart(e.target.value)} /></label>
            <label>Bis<input type="date" value={periodEnd} onChange={e=>setPeriodEnd(e.target.value)} /></label>
          </div>
          <Totals items={normalizeRows()} />
          <button className="primary" onClick={generateFinalPdf}>Endabrechnung inkl. Belege erstellen</button>
        </div>
      ) : null}

      {view === "users" ? <Users /> : null}
    </div>
  );
}

function Auth() {
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [fullName, setFullName] = useState("");
  const [company, setCompany] = useState("");
  const [role, setRole] = useState("mitarbeiter");
  const [msg, setMsg] = useState("");

  async function submit(e) {
    e.preventDefault();
    setMsg("");
    try {
      if (mode === "login") {
        let loginEmail = email;
        if (!email.includes("@")) {
          const { data, error } = await supabase.rpc("get_email_for_username", { p_username: email });
          if (error) throw error;
          loginEmail = data;
        }
        const { error } = await supabase.auth.signInWithPassword({ email: loginEmail, password });
        if (error) throw error;
      } else {
        const { error } = await supabase.auth.signUp({
          email,
          password,
          options: { data: { username, full_name: fullName, company_name: company, role } }
        });
        if (error) throw error;
        setMsg("Registrierung gesendet. Bitte einloggen oder E-Mail bestätigen.");
      }
    } catch (err) { setMsg(err.message || String(err)); }
  }

  return <div className="auth">
    <div className="card">
      <h1>Casa Tua Bautagebuch</h1>
      <p className="version">{APP_VERSION}</p>
      <div className="tabs small">
        <button className={mode==="login"?"active":""} onClick={()=>setMode("login")}>Login</button>
        <button className={mode==="register"?"active":""} onClick={()=>setMode("register")}>Registrieren</button>
      </div>
      <form onSubmit={submit}>
        {mode === "register" && <>
          <label>Benutzername<input value={username} onChange={e=>setUsername(e.target.value)} required /></label>
          <label>Name<input value={fullName} onChange={e=>setFullName(e.target.value)} /></label>
          <label>Firma<input value={company} onChange={e=>setCompany(e.target.value)} /></label>
          <label>Rolle<select value={role} onChange={e=>setRole(e.target.value)}>
            <option value="mitarbeiter">Mitarbeiter</option>
            <option value="dienstleister">Dienstleister</option>
            <option value="kraftwerk">Kraftwerk</option>
          </select></label>
        </>}
        <label>{mode==="login" ? "E-Mail oder Benutzername" : "E-Mail"}<input value={email} onChange={e=>setEmail(e.target.value)} required /></label>
        <label>Passwort<input type="password" value={password} onChange={e=>setPassword(e.target.value)} required /></label>
        <button className="primary">{mode==="login" ? "Einloggen" : "Registrieren"}</button>
      </form>
      {msg && <div className="msg">{msg}</div>}
    </div>
  </div>
}

function Field({ label, children }) { return <label>{label}{children}</label>; }
function BillingSelect({ form, setForm }) {
  return <Field label="Abrechnungsart">
    <select value={form.billing_type || "rechnung"} onChange={e=>setForm({...form,billing_type:e.target.value})}>
      {BILLING_TYPES.map(b=><option key={b.value} value={b.value}>{b.label}</option>)}
    </select>
  </Field>;
}

function EntryForm({ table, form, setForm, file, setFile, saveEntry, editId, cancelEdit }) {
  const update = (k,v) => setForm({ ...form, [k]: v });
  return <div className="card">
    <h2>{editId ? "Eintrag bearbeiten" : "Neuer Eintrag"} – {TABLES[table]}</h2>
    <form onSubmit={saveEntry}>
      <div className="grid">
        {table === "work_logs" && <>
          <Field label="Datum"><input type="date" value={form.work_date || ""} onChange={e=>update("work_date",e.target.value)} required /></Field>
          <Field label="Person"><input value={form.person_name || ""} onChange={e=>update("person_name",e.target.value)} /></Field>
          <Field label="Firma"><input value={form.company_name || ""} onChange={e=>update("company_name",e.target.value)} /></Field>
          <Field label="Kategorie / Bereich"><input value={form.area || ""} onChange={e=>update("area",e.target.value)} required /></Field>
          <Field label="Tätigkeit / Beschreibung"><textarea value={form.activity || ""} onChange={e=>update("activity",e.target.value)} required /></Field>
          <Field label="Stunden"><input type="number" step="0.25" value={form.hours || ""} onChange={e=>update("hours",e.target.value)} required /></Field>
          <Field label="Ansatz CHF"><input type="number" step="0.05" value={form.hourly_rate || ""} onChange={e=>update("hourly_rate",e.target.value)} /></Field>
          <BillingSelect form={form} setForm={setForm} />
        </>}
        {table === "costs" && <>
          <Field label="Datum"><input type="date" value={form.cost_date || ""} onChange={e=>update("cost_date",e.target.value)} required /></Field>
          <Field label="Lieferant"><input value={form.supplier || ""} onChange={e=>update("supplier",e.target.value)} required /></Field>
          <Field label="Person"><input value={form.person_name || ""} onChange={e=>update("person_name",e.target.value)} /></Field>
          <Field label="Firma"><input value={form.company_name || ""} onChange={e=>update("company_name",e.target.value)} /></Field>
          <Field label="Kategorie"><input value={form.category || ""} onChange={e=>update("category",e.target.value)} required /></Field>
          <Field label="Beschreibung"><textarea value={form.description || ""} onChange={e=>update("description",e.target.value)} required /></Field>
          <Field label="Rechnungsnummer"><input value={form.invoice_no || ""} onChange={e=>update("invoice_no",e.target.value)} /></Field>
          <Field label="Netto"><input type="number" step="0.05" value={form.net_amount || ""} onChange={e=>update("net_amount",e.target.value)} /></Field>
          <Field label="MWST"><input type="number" step="0.05" value={form.vat_amount || ""} onChange={e=>update("vat_amount",e.target.value)} /></Field>
          <Field label="Zahlung"><select value={form.payment_status || "offen"} onChange={e=>update("payment_status",e.target.value)}>{PAYMENT_OPTIONS.map(x=><option key={x} value={x}>{x}</option>)}</select></Field>
          <BillingSelect form={form} setForm={setForm} />
        </>}
        {table === "orders" && <>
          <Field label="Bestelldatum"><input type="date" value={form.order_date || ""} onChange={e=>update("order_date",e.target.value)} required /></Field>
          <Field label="Lieferant"><input value={form.supplier || ""} onChange={e=>update("supplier",e.target.value)} required /></Field>
          <Field label="Person"><input value={form.person_name || ""} onChange={e=>update("person_name",e.target.value)} /></Field>
          <Field label="Firma"><input value={form.company_name || ""} onChange={e=>update("company_name",e.target.value)} /></Field>
          <Field label="Kategorie"><input value={form.category || ""} onChange={e=>update("category",e.target.value)} required /></Field>
          <Field label="Beschreibung"><textarea value={form.description || ""} onChange={e=>update("description",e.target.value)} required /></Field>
          <Field label="Bestellt durch"><input value={form.ordered_by || ""} onChange={e=>update("ordered_by",e.target.value)} /></Field>
          <Field label="Erwartete Lieferung"><input type="date" value={form.expected_delivery || ""} onChange={e=>update("expected_delivery",e.target.value)} /></Field>
          <Field label="Geliefert am"><input type="date" value={form.delivered_date || ""} onChange={e=>update("delivered_date",e.target.value)} /></Field>
          <Field label="Betrag"><input type="number" step="0.05" value={form.amount || ""} onChange={e=>update("amount",e.target.value)} /></Field>
          <Field label="Status"><select value={form.order_status || "bestellt"} onChange={e=>update("order_status",e.target.value)}>
            {["angefragt","bestellt","teilgeliefert","geliefert","montiert","storniert"].map(x=><option key={x} value={x}>{x}</option>)}
          </select></Field>
          <Field label="Zahlung"><select value={form.payment_status || "offen"} onChange={e=>update("payment_status",e.target.value)}>{PAYMENT_OPTIONS.map(x=><option key={x} value={x}>{x}</option>)}</select></Field>
          <BillingSelect form={form} setForm={setForm} />
        </>}
        <Field label="Abrechnungsstatus"><select value={form.status || "offen"} onChange={e=>update("status",e.target.value)}>{STATUS_OPTIONS.map(x=><option key={x} value={x}>{x}</option>)}</select></Field>
        <Field label="PDF / Foto Beleg"><input id="receipt_file" type="file" accept=".pdf,.png,.jpg,.jpeg" onChange={e=>setFile(e.target.files?.[0] || null)} /></Field>
      </div>
      <div className="actions">
        <button className="primary" type="submit">{editId ? "Änderungen speichern" : "Speichern"}</button>
        {editId && <button type="button" onClick={cancelEdit}>Abbrechen</button>}
      </div>
    </form>
  </div>;
}

function EntriesTable({ table, rows, editRow, deleteRow, openReceipt }) {
  return <div className="card">
    <h2>Einträge</h2>
    <div className="tablewrap"><table>
      <thead><tr>
        <th>Datum</th><th>Name/Lieferant</th><th>Kategorie</th><th>Beschreibung</th><th>Abrechnung</th><th>Zahlung</th><th>Betrag</th><th>Beleg</th><th>Aktionen</th>
      </tr></thead>
      <tbody>{(rows || []).map(r => {
        const date = r.work_date || r.cost_date || r.order_date;
        const name = r.person_name || r.supplier || r.company_name || "";
        const cat = table === "work_logs" ? r.area : r.category;
        const desc = table === "work_logs" ? r.activity : r.description;
        const amt = table === "work_logs" ? (Number(r.hours||0)*Number(r.hourly_rate||0)) : table === "costs" ? (Number(r.net_amount||0)+Number(r.vat_amount||0)) : Number(r.amount||0);
        return <tr key={r.id}>
          <td>{date}</td><td>{name}</td><td>{cat}</td><td>{desc}</td>
          <td>{BILLING_TYPES.find(b=>b.value===(r.billing_type || ""))?.label || r.billing_type || "-"}</td>
          <td>{r.payment_status || "-"}</td><td>{fmt(amt)}</td>
          <td>{r.receipt_path ? <button onClick={()=>openReceipt(r.receipt_path)}>Beleg öffnen</button> : "-"}</td>
          <td><button onClick={()=>editRow(table,r)}>Bearbeiten</button> <button onClick={()=>deleteRow(table,r.id)}>Löschen</button></td>
        </tr>
      })}</tbody>
    </table></div>
  </div>
}

function Totals({ items }) {
  return <div className="totals">
    {BILLING_TYPES.map(bt => {
      const total = items.filter(i=>i.type===bt.value).reduce((s,i)=>s+i.amount,0);
      return <div className="total" key={bt.value}><b>{bt.label}</b><span>{fmt(total)}</span></div>
    })}
    <div className="total grand"><b>Gesamttotal</b><span>{fmt(items.reduce((s,i)=>s+i.amount,0))}</span></div>
  </div>
}

function Users() {
  const [users, setUsers] = useState([]);
  const [msg, setMsg] = useState("");
  useEffect(()=>{ load(); }, []);
  async function load() {
    const { data, error } = await supabase.from("profiles").select("*").order("created_at", { ascending: false });
    if (error) setMsg(error.message); else setUsers(data || []);
  }
  async function update(id, patch) {
    const { error } = await supabase.from("profiles").update(patch).eq("id", id);
    if (error) setMsg(error.message); else load();
  }
  return <div className="card">
    <h2>Zugänge</h2>{msg && <div className="msg">{msg}</div>}
    <div className="tablewrap"><table><thead><tr><th>Name</th><th>E-Mail</th><th>Firma</th><th>Rolle</th><th>Aktiv</th></tr></thead>
      <tbody>{users.map(u=><tr key={u.id}>
        <td>{u.full_name || u.username}</td><td>{u.email}</td><td>{u.company_name}</td>
        <td><select value={u.role} onChange={e=>update(u.id,{role:e.target.value})}>{["superadmin","admin","kraftwerk","dienstleister","mitarbeiter","viewer"].map(r=><option key={r} value={r}>{r}</option>)}</select></td>
        <td><input type="checkbox" checked={!!u.is_active} onChange={e=>update(u.id,{is_active:e.target.checked, approved_at:e.target.checked ? new Date().toISOString() : null})} /></td>
      </tr>)}</tbody>
    </table></div>
  </div>
}

createRoot(document.getElementById("root")).render(<App />);
